import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    getAiImage,
    getAiVideo
} from "@/templates/software/jira/logged-out/jira-software-logged-out-page/src/sections/productionized/components/assets";

import { Intelligence } from "./assets";

export const Ai = () => {
    const { formatMessage, locale } = useIntl();

    const title = formatMessage({
        id: "jira-product-tour.ai-section.title-text",
        defaultMessage: "Your next move, suggested by AI"
    });
    const subTitle = formatMessage({
        id: "jira-product-tour.ai-section.subtitle-text",
        defaultMessage:
            "Atlassian Intelligence takes your big ideas and automatically suggests the tasks to help get it done."
    });
    const buttonText = formatMessage({
        id: "jira-product-tour.ai-section.button-text",
        defaultMessage: "Explore Atlassian Intelligence"
    });
    const videoLink = getAiVideo(locale);
    const imageLink = getAiImage(locale);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = () => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: "atlas-55799-ai-button-clicked",
                section: "ai-section"
            }
        }).fire();
    };

    return (
        <div>
            <div css={aiSectionStyles}>
                <Intelligence />
                <h3 css={titleStyles}>{title}</h3>
                <div css={subTitleStyles}>{subTitle}</div>
                <div css={buttonStyles}>
                    <Adg4Button
                        variant="secondary"
                        color="white"
                        colorAgainst="other"
                        size="large"
                        href={useLocalizedHref("/software/jira/ai")}
                        onClick={handleClick}
                        dataEventProps={{
                            "data-product-key": "jira",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "ctaSection",
                            "data-label": "explore-atlassian-intelligence",
                            "data-label-english": `${buttonText}`,
                            "data-track-event":
                                "jira-pt.ai-section.explore-atlassian-intelligence"
                        }}
                    >
                        {buttonText}
                    </Adg4Button>
                </div>
                <video
                    css={videoStyles}
                    playsInline={true}
                    muted={true}
                    preload="auto"
                    autoPlay={true}
                    loop={true}
                    controls={true}
                >
                    <source src={videoLink} type="video/mp4" />
                </video>
                <Picture
                    css={imageStyles}
                    image={{
                        src: imageLink,
                        alt: "",
                        width: 2752,
                        height: 1838
                    }}
                />
            </div>
            <div css={footerStyles} />
        </div>
    );
};

const aiSectionStyles = css({
    display: "grid",
    justifyItems: "center",
    backgroundColor: wacToken("color.primary.blue"),
    color: "white",
    padding: "64px 10%",
    gap: "32px"
});

const imageStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "100%",
        height: "auto",
        padding: "16px",
        [media.above.lg]: {
            display: "none"
        }
    }
});

const videoStyles = css({
    maxWidth: "1187px",
    padding: "16px",
    [media.above.md]: {
        minHeight: "600px"
    },
    [media.below.lg]: {
        display: "none"
    }
});

const titleStyles = css({
    fontSize: "48px",
    fontWeight: "700",
    lineHeight: 1.5,
    margin: 0,
    textAlign: "center",
    [media.only.md]: {
        fontSize: "32px"
    },
    [media.only.sm]: {
        fontSize: "32px"
    },
    [media.below.sm]: {
        fontSize: "24px"
    }
});

const subTitleStyles = css({
    color: wacToken("color.neutral.light0"),
    textAlign: "center",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    fontWeight: "400"
});

const buttonStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["a"]: {
        borderRadius: "4px",
        [media.below.md]: {
            fontSize: "16px"
        }
    }
});

const footerStyles = css({
    minHeight: "180px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center 0",
    backgroundSize: "100vw 180px",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none' viewBox='0 0 1451 143'%3E%3Cpath d='M819 102.7V72l632 30.7V143l-632-40.3Z' style='fill:%23ffa900'/%3E%3Cpath d='M0 0h1451v114L0 62.3V0Z' style='fill:%231868db'/%3E%3C/svg%3E")`,
    [media.below.md]: {
        minHeight: "60px",
        backgroundSize: "100vw 60px"
    }
});
